import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const MONTH_FULL = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const fmt = (v) => v === null || v === undefined ? '-' : new Intl.NumberFormat('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtDec = (v) => v === null || v === undefined ? '-' : new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

export default function NCCustomerKPIPage() {
  useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMonth, setExpandedMonth] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/indicator-nc-customer', { params: { year } });
      setData(res.data);
    } catch { toast.error('Errore caricamento dati'); } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    let totalCount = 0, totalCost = 0, monthsWithData = 0;
    data.forEach(m => { totalCount += m.count; totalCost += m.total_cost; if (m.count > 0) monthsWithData++; });
    return { totalCount, totalCost, avgCost: totalCount > 0 ? totalCost / totalCount : 0, monthsWithData };
  }, [data]);

  const chartBar = useMemo(() => data.map(m => ({ name: MONTHS[m.month - 1], 'NC Cliente': m.count })), [data]);
  const chartLine = useMemo(() => data.map(m => ({ name: MONTHS[m.month - 1], 'Costo Totale': m.total_cost > 0 ? m.total_cost : null })), [data]);

  return (
    <div className="space-y-5" data-testid="nc-customer-kpi-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="nc-customer-title">IND003 – Non conformità cliente</h1>
          <p className="text-sm text-slate-500 mt-0.5">Analisi automatica dalle NC registrate</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-1">
          <button onClick={() => setYear(y => y - 1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="year-prev"><ChevronLeft className="w-4 h-4 text-slate-600" /></button>
          <span className="text-sm font-bold text-slate-900 min-w-[4rem] text-center" data-testid="year-display">{year}</span>
          <button onClick={() => setYear(y => y + 1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="year-next"><ChevronRight className="w-4 h-4 text-slate-600" /></button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="summary-kpis">
        <KpiCard label="Totale NC Cliente" value={summary.totalCount} testId="kpi-count" accent={summary.totalCount === 0 ? 'text-emerald-600' : 'text-red-600'} />
        <KpiCard label="Obiettivo" value="0 NC" testId="kpi-target" accent="text-emerald-600" />
        <KpiCard label="Scostamento" value={summary.totalCount > 0 ? `+${summary.totalCount} NC` : 'In obiettivo'} testId="kpi-diff" accent={summary.totalCount === 0 ? 'text-emerald-600' : 'text-red-600'} />
        <KpiCard label="Costo Totale" value={fmtDec(summary.totalCost)} unit="EUR" testId="kpi-cost" />
        <KpiCard label="Costo Medio / NC" value={fmtDec(summary.avgCost)} unit="EUR" testId="kpi-avg" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="nc-bar-chart">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Numero NC Cliente / Mese</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartBar} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
              <Bar dataKey="NC Cliente" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="nc-line-chart">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Costo Mensile NC Cliente</h2>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartLine} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => fmt(v)} />
              <Tooltip formatter={(v) => [fmtDec(v) + ' EUR']} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
              <Line type="monotone" dataKey="Costo Totale" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 4, fill: '#0f172a' }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="nc-customer-table">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-2.5 w-8"></th>
                <th className="px-4 py-2.5 w-32">Mese</th>
                <th className="px-4 py-2.5 text-right w-28">N. NC Cliente</th>
                <th className="px-4 py-2.5 text-right w-24">Obiettivo</th>
                <th className="px-4 py-2.5 text-right w-24">Diff.</th>
                <th className="px-4 py-2.5 text-right w-36">Costo Totale</th>
                <th className="px-4 py-2.5 text-right w-36">Costo Medio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map(m => (
                <React.Fragment key={m.month}>
                  <tr
                    className={`transition-colors cursor-pointer ${m.count > 0 ? 'hover:bg-slate-50' : ''} ${expandedMonth === m.month ? 'bg-slate-50' : ''}`}
                    onClick={() => m.count > 0 && setExpandedMonth(expandedMonth === m.month ? null : m.month)}
                    data-testid={`row-${m.month}`}
                  >
                    <td className="px-4 py-2.5 text-slate-400">
                      {m.count > 0 && (expandedMonth === m.month ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-700">{MONTH_FULL[m.month - 1]}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`font-mono font-semibold ${m.count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{m.count}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-500">0</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`font-mono font-semibold ${m.count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{m.count > 0 ? `+${m.count}` : 'OK'}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">{m.total_cost > 0 ? fmtDec(m.total_cost) + ' EUR' : '-'}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{m.avg_cost > 0 ? fmtDec(m.avg_cost) + ' EUR' : '-'}</td>
                  </tr>
                  {expandedMonth === m.month && m.ncs.length > 0 && (
                    <tr><td colSpan={7} className="px-0 py-0">
                      <div className="bg-slate-50 border-t border-b border-slate-200 px-8 py-3" data-testid={`detail-${m.month}`}>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-slate-500 uppercase tracking-wide">
                              <th className="pb-1.5 pr-3">NC</th>
                              <th className="pb-1.5 pr-3">Cliente</th>
                              <th className="pb-1.5 pr-3">Prodotto</th>
                              <th className="pb-1.5 pr-3">Lotto</th>
                              <th className="pb-1.5 pr-3 text-right">Costo</th>
                              <th className="pb-1.5 pr-3">Stato</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {m.ncs.map(nc => (
                              <tr key={nc.id} className="text-slate-700">
                                <td className="py-1.5 pr-3 font-mono font-semibold">{nc.nc_number}</td>
                                <td className="py-1.5 pr-3">{nc.client_name}</td>
                                <td className="py-1.5 pr-3">{nc.product_code} - {nc.product_name}</td>
                                <td className="py-1.5 pr-3 font-mono">{nc.lot_code}</td>
                                <td className="py-1.5 pr-3 text-right font-mono">{fmtDec(nc.total_cost)} EUR</td>
                                <td className="py-1.5 pr-3"><StatusBadge status={nc.status} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-semibold text-sm border-t-2 border-slate-300">
                <td className="px-4 py-2.5"></td>
                <td className="px-4 py-2.5 text-slate-900">Totale</td>
                <td className="px-4 py-2.5 text-right font-mono text-red-600" data-testid="total-count">{summary.totalCount}</td>
                <td className="px-4 py-2.5 text-right font-mono text-slate-500">0</td>
                <td className="px-4 py-2.5 text-right font-mono" data-testid="total-diff">
                  <span className={summary.totalCount > 0 ? 'text-red-600' : 'text-emerald-600'}>{summary.totalCount > 0 ? `+${summary.totalCount}` : 'OK'}</span>
                </td>
                <td className="px-4 py-2.5 text-right font-mono" data-testid="total-cost">{fmtDec(summary.totalCost)} EUR</td>
                <td className="px-4 py-2.5 text-right font-mono" data-testid="total-avg">{fmtDec(summary.avgCost)} EUR</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, unit, accent, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={testId}>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold mt-1 ${accent || 'text-slate-900'}`}>{value}</p>
      {unit && <p className="text-xs text-slate-400 mt-0.5">{unit}</p>}
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = { open: 'bg-red-100 text-red-700', in_progress: 'bg-amber-100 text-amber-700', closed: 'bg-emerald-100 text-emerald-700' };
  const labels = { open: 'Aperta', in_progress: 'In corso', closed: 'Chiusa' };
  return <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full ${colors[status] || 'bg-slate-100 text-slate-600'}`}>{labels[status] || status}</span>;
}
